import { prefersReducedMotion } from '@theme/utilities';

/**
 * Lightweight SplitText-compatible char splitter.
 * Used when the GSAP SplitText Club plugin is not available.
 */
class SplitTextChars {
  /**
   * @param {HTMLElement} element
   * @param {{ charsClass?: string }} options
   */
  constructor(element, { charsClass = 'footer-intro__wave-char' } = {}) {
    /** @type {HTMLElement[]} */
    this.chars = [];
    const text = element.textContent ?? '';

    element.textContent = '';

    for (const char of text) {
      const span = document.createElement('span');
      span.className = charsClass;
      span.setAttribute('aria-hidden', 'true');
      span.style.position = 'relative';
      span.style.display = 'inline-block';
      span.textContent = char === ' ' ? '\u00A0' : char;
      element.appendChild(span);
      this.chars.push(span);
    }
  }
}

/**
 * @param {string} src
 * @returns {Promise<void>}
 */
function loadScript(src) {
  if (window.gsap) {
    return Promise.resolve();
  }

  const existing = document.querySelector(`script[src="${src}"]`);

  if (existing) {
    return new Promise((resolve, reject) => {
      if (window.gsap) {
        resolve();
        return;
      }

      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

/**
 * @param {HTMLElement} waveText
 */
function initWaveText(waveText) {
  if (waveText.dataset.waveInitialized === 'true') {
    return;
  }

  waveText.dataset.waveInitialized = 'true';

  const SplitTextImpl = window.SplitText ?? SplitTextChars;

  if (window.SplitText && window.gsap?.registerPlugin) {
    window.gsap.registerPlugin(window.SplitText);
  }

  const split = new SplitTextImpl(waveText, {
    type: 'chars',
    charsClass: 'footer-intro__wave-char',
  });

  const { chars } = split;

  if (!chars?.length) {
    return;
  }

  window.gsap.set(chars, { y: 0 });

  window.gsap
    .timeline({
      repeat: -1,
      repeatDelay: 0,
    })
    .to(chars, {
      y: -20,
      duration: 0.6,
      ease: 'sine.inOut',
      stagger: {
        each: 0.1,
        from: 'start',
        yoyo: true,
        repeat: 1,
      },
    });
}

/**
 * Footer wave text animation powered by GSAP.
 */
class FooterIntro extends HTMLElement {
  async connectedCallback() {
    const waveText = this.querySelector('.footer-intro__wave-text[data-footer-intro-wave-text]');

    if (!waveText || prefersReducedMotion()) {
      return;
    }

    const gsapSrc = this.dataset.gsapSrc;

    if (!gsapSrc) {
      console.warn('Footer intro: missing GSAP source');
      return;
    }

    try {
      await loadScript(gsapSrc);

      if (!window.gsap) {
        console.warn('GSAP not found');
        return;
      }

      initWaveText(/** @type {HTMLElement} */ (waveText));
    } catch (error) {
      console.warn('Footer intro animation failed to initialize', error);
    }
  }
}

if (!customElements.get('footer-intro')) {
  customElements.define('footer-intro', FooterIntro);
}
